import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { Button } from '@mui/material';
import { getDocsUpload, uploadSelfie } from "../../Utils/api";
import { toast } from "react-toastify";
import { updateJourneyEvents } from "../../CustomerJourneyDetails/CustomerJourneyDetails";
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { useNavigate } from "react-router-dom";

export default function UploadDocument() {
  const journeySteps = useSelector((state) => state.customerJourneyDetails.journeySteps);
  const dispatch = useDispatch();
  const customerDetails = useSelector((state) => state.customerJourneyDetails.customerDetails);
  const navigate = useNavigate();

  const [selectedFiles, setSelectedFiles] = useState({});
  const [fileSizeErrors, setFileSizeErrors] = useState({});
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [loader, setLoader] = useState(false);


  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const fileInputRefs = {
    aadhaar_front: useRef(null),
    aadhaar_rear: useRef(null),
    pan: useRef(null),
    pay_slip: useRef(null),
    bank_statement: useRef(null),
    residence_proof: useRef(null),
  };

  const docTypeIdMap = {
    aadhaar_front: 1,
    aadhaar_rear: 2,
    pan: 4,
    bank_statement: 6,
    residence_proof: 8,
    pay_slip: 16,
    selfie: 18,
  };

  const getExtensionFromFile = (f) => {
    if (!f) return "";
    if (f.name) {
      const idx = f.name.lastIndexOf('.');
      if (idx !== -1) return f.name.substring(idx + 1).toLowerCase();
    }
    if (f.type) {
      const map = { 'image/jpeg': 'jpg', 'image/png': 'png', 'application/pdf': 'pdf' };
      if (map[f.type]) return map[f.type];
      const parts = f.type.split('/');
      if (parts.length === 2) return parts[1];
    }
    return "";
  };

  const handleClick = (docType) => {
    fileInputRefs[docType]?.current?.click();
  };

  const handleChange = (event, docType) => {
    const file = event.target.files[0];
    const maxSizeInBytes = 5 * 1024 * 1024;

    if (file) {
      if (file.size > maxSizeInBytes) {
        setFileSizeErrors((prev) => ({ ...prev, [docType]: "File size should be less than 5MB." }));
        toast.error("File size should be less than 5MB.");
        return;
      }

      setFileSizeErrors((prev) => ({ ...prev, [docType]: null }));
      setSelectedFiles((prev) => ({ ...prev, [docType]: file }));
    }
  };

  const openCamera = async () => {
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      toast.error("Unable to access camera.");
      console.error(err);
    }
  };

  const captureSelfie = () => {
    const context = canvasRef.current.getContext("2d");
    context.drawImage(videoRef.current, 0, 0, 250, 150);

    canvasRef.current.toBlob((blob) => {
      if (blob.size > 2 * 1024 * 1024) {
        toast.error("Selfie size should be less than 2MB.");
        return;
      }

      const selfieFile = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      setSelfieBlob(selfieFile);

      const previewUrl = URL.createObjectURL(selfieFile);
      setSelfiePreviewUrl(previewUrl);
    }, "image/jpeg");

    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    setShowCamera(false);
  };

  useEffect(() => {
    return () => {
      if (selfiePreviewUrl) {
        URL.revokeObjectURL(selfiePreviewUrl);
      }
    };
  }, [selfiePreviewUrl]);

  const renderUploadBlock = (label, docType) => {
    const isPdfOnly = docType === "bank_statement" || docType === "pay_slip";
    const acceptTypes = isPdfOnly ? "application/pdf" : "image/jpeg, image/png";

    return (
      <li className="mt-2" key={docType}>
        <div className="drop-container" onClick={() => handleClick(docType)}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'center'}}>
             <AddCircleOutlineIcon style={{ fontSize: '40px' }} />
          <span className="drop-title">{label}</span>
          </div>
         
          <div className="docinput-wrap">
            <input
              type="file"
              className="form-control"
              ref={fileInputRefs[docType]}
              onChange={(e) => handleChange(e, docType)}
              accept={acceptTypes}
              style={{ display: 'none' }}
            />
            {selectedFiles[docType] && (
              <div style={{ fontSize: '12px' }}>{selectedFiles[docType].name}</div>
            )}
          </div>

          {fileSizeErrors[docType] && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
              {fileSizeErrors[docType]}
            </div>
          )}
        </div>
      </li>
    );
  };

  const submit = async () => {
    // alert("hi");
    if (loader) return; // Prevent double click while loading

    setLoader(true); // Start loading

    try {
          const requiredDocs = ["aadhaar_front", "aadhaar_rear", "pan", "pay_slip", "bank_statement", "residence_proof"];
          const missingDocs = requiredDocs.filter((doc) => !selectedFiles[doc] && journeySteps[`${doc}_upload`] !== 1);

          if (missingDocs.length > 0) {
            toast.error("Please upload all required documents before continuing.");
            console.error("Missing documents:", missingDocs);
            setLoader(false);
            return;
          }

          if (!selfieBlob && journeySteps.selfie_upload !== 1) {
            toast.error("Please capture a selfie before continuing.");
            setLoader(false);
            return;
          }

          let uploadedDocTypes = {};
          let response;

          for (const [docType, file] of Object.entries(selectedFiles)) {
            if (journeySteps[`${docType}_upload`] === 1) continue;

            const formData = new FormData();
            formData.append("profileId", customerDetails?.profileId);
            formData.append("profile_id", customerDetails?.profileId);
            formData.append("docTypeId", docTypeIdMap[docType]);
            formData.append("paySlip", file);
            formData.append("file", file);
            const fileExt = getExtensionFromFile(file);
            formData.append("file_ext", fileExt);

            if(docTypeIdMap[docType] == 1 || docTypeIdMap[docType] == 2){
              formData.append("event_name", 'aadhaar_upload');
            } else if(docTypeIdMap[docType] == 4){
              formData.append("event_name", 'pan_upload');
            } else if(docTypeIdMap[docType] == 16){
              formData.append("event_name", 'pay_slip_upload');
            } else if(docTypeIdMap[docType] == 6){
               formData.append("event_name", 'bank_statement_upload');
            }else if(docTypeIdMap[docType] == 8){
               formData.append("event_name", 'residence_proof_upload');
            }
            formData.append("doc_type", docType);
            

            response = await getDocsUpload(formData);

            if (response?.status === 1) {
              toast.success(`${docType} uploaded successfully.`);
              uploadedDocTypes[docType] = true;
            } else {
              toast.error(`${docType} upload failed: ${response?.message || 'Something went wrong'}`);
              setLoader(false);
              return;
            }
          }

          if (!journeySteps.selfie_upload && selfieBlob) {
            const selfieFormData = new FormData();
            selfieFormData.append("profileId", customerDetails?.profileId);
            selfieFormData.append("profile_id", customerDetails?.profileId);
            selfieFormData.append("docTypeId", docTypeIdMap.selfie);
            selfieFormData.append("paySlip", selfieBlob);
            selfieFormData.append("file", selfieBlob);
            selfieFormData.append("event_name", 'selfie_upload');
            selfieFormData.append("file_ext", getExtensionFromFile(selfieBlob));

            const selfieResponse = await uploadSelfie(selfieFormData);

            if (selfieResponse?.status === 1) {
              toast.success("Selfie uploaded successfully.");
              uploadedDocTypes["selfie"] = true;
            } else {
              toast.error(`Selfie upload failed: ${selfieResponse?.message || 'Something went wrong'}`);
              setLoader(false);
              return;
            }
          }

          const updatedState = {};

          if ((uploadedDocTypes.aadhaar_front || journeySteps.aadhaar_upload) &&
            (uploadedDocTypes.aadhaar_rear || journeySteps.aadhaar_upload)) {
            updatedState.aadhaar_upload = 1;
          }
          if (uploadedDocTypes.pan) updatedState.pan_upload = 1;
          if (uploadedDocTypes.pay_slip) updatedState.pay_slip_upload = 1;
          if (uploadedDocTypes.bank_statement) updatedState.bank_statement_upload = 1;
          if (uploadedDocTypes.residence_proof) updatedState.residence_proof_upload = 1;
          if (uploadedDocTypes.selfie) updatedState.selfie_upload = 1;

          if (Object.keys(updatedState).length > 0) {
            updatedState.upload_documents = 1;
            dispatch(updateJourneyEvents(updatedState));
          }

         navigate('/journey/congratulations');
    } catch (error) {
      console.error("Error submitting documents:", error);
      toast.error("Error submitting documents");
    } finally {
      setLoader(false); // Stop loading at end of try or catch
    }
  };


  return (
    <>
      {journeySteps.upload_documents === 2 && (
        <div className='box-height' style={{ display: 'flex', alignItems: 'start', justifyContent: 'center', width: '100%' }}>
          <div className="wraper-right-box" style={{ width: '100%' }}>
             
            <ul className="upload-doc-wraper">
              {journeySteps.aadhaar_upload === 0 && renderUploadBlock("Aadhaar Front", "aadhaar_front")}
              {journeySteps.aadhaar_upload === 0 && renderUploadBlock("Aadhaar Rear", "aadhaar_rear")}
              {journeySteps.pan_upload === 0 && renderUploadBlock("PAN Card", "pan")}
              {journeySteps.pay_slip_upload === 0 && renderUploadBlock(" Latest Month Pay Slip", "pay_slip")}
              {journeySteps.bank_statement_upload === 0 && renderUploadBlock("Latest 3 Months Bank Statement", "bank_statement")}
              {journeySteps.residence_proof_upload === 0 && renderUploadBlock("Current Residence Proof", "residence_proof")}
            </ul>

            {/* Selfie Section */}
            <div className="Upload_selfi mt-3 p-4 d-flex align-items-center rounded-2 justify-content-center gap-4">
              {showCamera ? (
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <video ref={videoRef} width="250" height="150" />
                  <Button className="mt-3" onClick={captureSelfie} variant="contained" color="primary">
                    Capture Selfie
                  </Button>
                  <canvas ref={canvasRef} width="250" height="150" style={{ display: 'none' }} />
                </div>
              ) : (
                <>
                  <div className="card-Pic" onClick={openCamera}>
                    <div className="drop-container">
                      {selfiePreviewUrl ? (
                        <img
                          src={selfiePreviewUrl}
                          alt="Selfie Preview"
                          style={{ objectFit: 'contain', borderRadius: "8px", height: '100%', width: '100%' }}
                        />
                      ) : (
                        <>
                          <AddCircleOutlineIcon style={{ fontSize: '40px' }} />
                          <span className="drop-title" style={{ textAlign: 'center' }}>
                            No selfie taken
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {/* <Button
                    variant="outlined"
                    className='btn ms-bg-primary p-2 text-white'
                    onClick={openCamera}
                  >
                    <AddAPhotoOutlinedIcon /> Take a Selfie
                  </Button> */}
                </>
              )}
            </div>

            <Button
              type="submit"
              variant='contained'
              onClick={submit}
              className='mt-5 fw-bold'
              size='large'
              style={{ background: '#9a141a' }}
              disabled={loader}
            >
              {loader ? 'Continuing...' : 'Continue'}  &nbsp;&nbsp;
              <SendIcon />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
